/** Web Push + Notification helpers for support tickets (admin + portal). */

const notifiedIds = new Set<string>();

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function isIosDevice(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPadOS 13+ may report as MacIntel with touch.
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function isStandalonePwa(): boolean {
  if (typeof window === 'undefined') return false;
  const mq = window.matchMedia('(display-mode: standalone)').matches;
  const ios = 'standalone' in navigator && Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
  return mq || ios;
}

/** Human-readable reason when pushSupported() is false. */
export function pushUnsupportedHint(): string {
  if (isIosDevice()) {
    if (!isStandalonePwa()) {
      return (
        'На iPhone Web Push в обычной вкладке Safari/Chrome не работает. ' +
        'Поделиться → «На экран «Домой»» → откройте иконку с Домой → снова зайдите в поддержку и включите уведомления. ' +
        'Нужен iOS 16.4+.'
      );
    }
    return 'Web Push недоступен в этом приложении. Обновите iOS до 16.4+ (или новее) и откройте сайт с ярлыка на Домой.';
  }
  return 'Этот браузер не поддерживает Web Push.';
}

export async function registerSupportServiceWorker(swUrl: string): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    // Must stay under the SW script path (/user/ or /vpnbotpanel/).
    // new URL('.', location) breaks on "/user" without trailing slash → scope "/".
    const base = String(import.meta.env.BASE_URL || '/');
    const scope = base.endsWith('/') ? base : `${base}/`;
    const reg = await navigator.serviceWorker.register(swUrl, { scope });
    await navigator.serviceWorker.ready;
    return reg;
  } catch {
    return null;
  }
}

export type PushApi = {
  getVapidPublicKey: () => Promise<{ success: boolean; data?: { public_key: string }; error?: { message: string } }>;
  subscribePush: (sub: { endpoint: string; keys: { p256dh: string; auth: string } }) => Promise<{ success: boolean; error?: { message: string } }>;
  unsubscribePush: (endpoint: string) => Promise<{ success: boolean; error?: { message: string } }>;
};

function subscriptionKeys(sub: PushSubscription): { p256dh: string; auth: string } | null {
  const json = sub.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!p256dh || !auth) return null;
  return { p256dh, auth };
}

export async function enableSupportPush(api: PushApi, swUrl: string): Promise<{ ok: boolean; message?: string }> {
  if (!pushSupported()) {
    return { ok: false, message: pushUnsupportedHint() };
  }
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    return { ok: false, message: 'Разрешение на уведомления не выдано' };
  }
  const reg = await registerSupportServiceWorker(swUrl);
  if (!reg) {
    return { ok: false, message: 'Не удалось зарегистрировать service worker' };
  }
  const vapid = await api.getVapidPublicKey();
  if (!vapid.success || !vapid.data?.public_key) {
    return { ok: false, message: vapid.error?.message || 'VAPID не настроен на сервере' };
  }
  // Always recreate subscription so it matches current VAPID keys.
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    try {
      await api.unsubscribePush(existing.endpoint);
    } catch {
      /* ignore */
    }
    try {
      await existing.unsubscribe();
    } catch {
      /* ignore */
    }
  }
  let sub: PushSubscription;
  try {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid.data.public_key) as BufferSource,
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'subscribe failed';
    return { ok: false, message: `Не удалось подписаться: ${msg}` };
  }
  const keys = subscriptionKeys(sub);
  if (!keys) {
    return { ok: false, message: 'Некорректная push-подписка' };
  }
  const res = await api.subscribePush({ endpoint: sub.endpoint, keys });
  if (!res.success) {
    return { ok: false, message: res.error?.message || 'Не удалось сохранить подписку' };
  }
  return { ok: true };
}

export async function disableSupportPush(api: PushApi): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await api.unsubscribePush(sub.endpoint);
    try {
      await sub.unsubscribe();
    } catch {
      /* ignore */
    }
  }
}

export async function getPushSubscriptionState(): Promise<'unsupported' | 'denied' | 'prompt' | 'subscribed' | 'off'> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission === 'default') return 'prompt';
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    return sub ? 'subscribed' : 'off';
  } catch {
    return 'off';
  }
}

function playNotifyBeep(): void {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.value = 0.0001;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const now = ctx.currentTime;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.12, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
    osc.start(now);
    osc.stop(now + 0.4);
    window.setTimeout(() => {
      void ctx.close();
    }, 500);
  } catch {
    /* ignore */
  }
}

export function maybeInAppSupportNotify(opts: {
  title: string;
  body: string;
  ticketId: number;
  selectedTicketId: number | null;
  dedupeKey: string;
  onToast?: (message: string) => void;
  onClick?: () => void;
}): void {
  const { title, body, ticketId, selectedTicketId, dedupeKey, onToast, onClick } = opts;
  if (notifiedIds.has(dedupeKey)) return;
  const focusedOnTicket =
    document.visibilityState === 'visible' && selectedTicketId === ticketId;
  if (focusedOnTicket) return;
  notifiedIds.add(dedupeKey);
  if (notifiedIds.size > 200) {
    const first = notifiedIds.values().next().value;
    if (first != null) notifiedIds.delete(first);
  }

  playNotifyBeep();
  onToast?.(`${title}\n${body}`);

  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    const n = new Notification(title, {
      body,
      tag: `support-${ticketId}`,
      requireInteraction: true,
    });
    if (onClick) {
      n.onclick = () => {
        window.focus();
        onClick();
        n.close();
      };
    }
  } catch {
    /* ignore */
  }
}

export function iosAddToHomeHint(): string | null {
  if (!isIosDevice() || isStandalonePwa()) return null;
  return 'На iPhone: Поделиться → «На экран «Домой»», затем откройте ярлык и включите уведомления.';
}
